import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setSession, isLoggedIn, AuthUser } from './auth';

// ─────────────────────────────────────────────────────────────────────────────
// Login / registration UI for the website.
//
// Mirrors the mobile InlineLoginGate look: a light rounded card with the logo,
// a "Sign in with Google" button (Google Identity Services → /auth/google-login)
// and an email/password form that toggles between login and registration.
//
//  • <LoginCard/>   – the card itself; embed it anywhere (e.g. a modal in the
//                     seat-map editor). Calls onSuccess(user) after auth.
//  • <LoginScreen/> – full-screen route wrapper (/login) that centers the card
//                     and honours a ?redirect=<path> query param.
// ─────────────────────────────────────────────────────────────────────────────

const API = 'https://maineventbackend.seuytel.com/api/';
const GOOGLE_WEB_CLIENT_ID =
    '1013567038484-65i3tp13ijp29n98gi5v7tbp22utgvno.apps.googleusercontent.com';

const FACEBOOK_APP_ID = '24847849601550999';

const ACCENT = '#4E6FD6';
const CARD_BG = '#F6F7FB';
const TEXT_DARK = '#1B2559';
const TEXT_MUTED = '#707EAE';
const FONT_FAMILY = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

type Mode = 'login' | 'register';
type ReactivationPayload = {
    status?: string;
    message?: string;
    reactivation_token?: string;
    reactivation?: { token?: string };
};

function isAccountReactivationRequired(payload: any): boolean {
    return (
        payload?.status === 'reactivation_required' ||
        payload?.message === 'ACCOUNT_DELETED_REACTIVATION_REQUIRED'
    );
}

function getReactivationToken(payload: ReactivationPayload): string | null {
    return payload.reactivation_token || payload.reactivation?.token || null;
}

// ── Google Identity Services loader (singleton) ──────────────────────────────
let gisPromise: Promise<any> | null = null;
function loadGoogleIdentity(): Promise<any> {
    if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
    const w = window as any;
    if (w.google?.accounts?.id) return Promise.resolve(w.google);
    if (gisPromise) return gisPromise;
    gisPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => resolve((window as any).google);
        script.onerror = () => reject(new Error('Failed to load Google sign-in'));
        document.head.appendChild(script);
    });
    return gisPromise;
}

let fbPromise: Promise<any> | null = null;

function loadFacebookSdk(): Promise<any> {
    if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
    const w = window as any;
    if (w.FB) return Promise.resolve(w.FB);
    if (fbPromise) return fbPromise;

    fbPromise = new Promise((resolve, reject) => {
        w.fbAsyncInit = function () {
            w.FB.init({
                appId: FACEBOOK_APP_ID,
                cookie: true,
                xfbml: false,
                version: 'v20.0',
            });
            resolve(w.FB);
        };

        const script = document.createElement('script');
        script.id = 'facebook-jssdk';
        script.src = 'https://connect.facebook.net/en_US/sdk.js';
        script.async = true;
        script.defer = true;
        script.onerror = () => reject(new Error('Failed to load Facebook SDK'));
        document.body.appendChild(script);
    });

    return fbPromise;
}

export interface LoginCardProps {
    title?: string;
    subtitle?: string;
    /** Called after a successful login or registration. */
    onSuccess?: (user: AuthUser) => void;
    /** When provided, a close (✕) button is shown (modal usage). */
    onClose?: () => void;
}

export const LoginCard: React.FC<LoginCardProps> = ({
    title = 'Willkommen bei Main Event',
    subtitle = 'Melde dich an, um deine Saalpläne zu speichern.',
    onSuccess,
    onClose,
}) => {
    const [mode, setMode] = useState<Mode>('login');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [reactivationPayload, setReactivationPayload] = useState<ReactivationPayload | null>(null);
const [reactivationBusy, setReactivationBusy] = useState(false);

    // Shared
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    // Register-only
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [anrede, setAnrede] = useState<1 | 2>(1); // 1 = Herr, 2 = Frau
    const [userType, setUserType] = useState<1 | 2>(1); // 1 = Privat, 2 = Veranstalter

    const googleBtnRef = useRef<HTMLDivElement>(null);

    const handleSession = useCallback(
        (data: any) => {
            setSession({
                user: data.user,
                token: data.token ?? data.access_token,
                expires_at: data.expires_at ?? null,
            });
            onSuccess?.(data.user);
        },
        [onSuccess],
    );

    const handleAuthResult = useCallback(
    async (res: Response, fallbackMessage: string) => {
        const data = await res.json();

        if (isAccountReactivationRequired(data)) {
            setReactivationPayload(data);
            return;
        }

        if (!res.ok) {
            throw new Error(data.details || data.detail || data.message || fallbackMessage);
        }

        handleSession(data);
    },
    [handleSession],
);

const handleReactivateAccount = useCallback(async () => {
    if (!reactivationPayload || reactivationBusy) return;

    const token = getReactivationToken(reactivationPayload);

    if (!token) {
        setError('Dieser Account kann aktuell nicht reaktiviert werden.');
        setReactivationPayload(null);
        return;
    }

    setReactivationBusy(true);
    setError(null);

    try {
        const res = await fetch(`${API}auth/reactivate-account`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ reactivation_token: token }),
        });

        const data = await res.json();

        if (!res.ok || !data?.user || !(data?.access_token || data?.token || data?.session_id)) {
            throw new Error(data?.message || 'Der Account konnte nicht reaktiviert werden.');
        }

        handleSession({
            user: data.user,
            token: data.access_token || data.token || data.session_id,
            expires_at: data.expires_at ?? null,
        });

        setReactivationPayload(null);
    } catch (e: any) {
        setError(e.message || 'Der Account konnte nicht reaktiviert werden.');
    } finally {
        setReactivationBusy(false);
    }
}, [handleSession, reactivationBusy, reactivationPayload]);

    // ── Google sign-in ───────────────────────────────────────────────────────
    const handleGoogleCredential = useCallback(
        async (idToken: string) => {
            setBusy(true);
            setError(null);
            try {
                const res = await fetch(`${API}auth/google-login`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id_token: idToken }),
                });
                await handleAuthResult(res, 'Google-Anmeldung fehlgeschlagen');
            } catch (e: any) {
                setError(e.message || 'Google-Anmeldung fehlgeschlagen');
            } finally {
                setBusy(false);
            }
        },
        [handleAuthResult],
    );

    const handleFacebookLogin = useCallback(async () => {
    if (busy) return;

    setBusy(true);
    setError(null);

    try {
        const FB = await loadFacebookSdk();

        const loginResponse: any = await new Promise((resolve) => {
            FB.login(
                (response: any) => resolve(response),
                { scope: 'public_profile,email' },
            );
        });

        const accessToken = loginResponse?.authResponse?.accessToken;

        if (!accessToken) {
            throw new Error('Facebook-Anmeldung wurde abgebrochen oder ist fehlgeschlagen.');
        }

        const res = await fetch(`${API}auth/facebook-login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ access_token: accessToken }),
        });

        await handleAuthResult(res, 'Facebook-Anmeldung fehlgeschlagen');
    } catch (e: any) {
        setError(e.message || 'Facebook-Anmeldung fehlgeschlagen');
    } finally {
        setBusy(false);
    }
}, [busy, handleAuthResult]);

    useEffect(() => {
        let cancelled = false;
        loadGoogleIdentity()
            .then((google) => {
                if (cancelled || !google?.accounts?.id || !googleBtnRef.current) return;
                google.accounts.id.initialize({
                    client_id: GOOGLE_WEB_CLIENT_ID,
                    callback: (resp: any) => {
                        if (resp?.credential) handleGoogleCredential(resp.credential);
                    },
                });
                googleBtnRef.current.innerHTML = '';
                google.accounts.id.renderButton(googleBtnRef.current, {
                    theme: 'outline',
                    size: 'large',
                    width: 320,
                    text: 'continue_with',
                    logo_alignment: 'center',
                });
            })
            .catch(() => {
                /* Google unavailable — email/password still works. */
            });
        return () => {
            cancelled = true;
        };
    }, [handleGoogleCredential]);

    // ── Email login / registration ───────────────────────────────────────────
    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (busy) return;
        setError(null);

        if (!email.trim() || !password) {
            setError('Bitte E-Mail und Passwort eingeben.');
            return;
        }
        if (mode === 'register') {
            if (!firstName.trim()) {
                setError('Bitte Vornamen eingeben.');
                return;
            }
            if (password.length < 6) {
                setError('Das Passwort muss mindestens 6 Zeichen lang sein.');
                return;
            }
            if (password !== passwordConfirm) {
                setError('Die Passwörter stimmen nicht überein.');
                return;
            }
        }

        setBusy(true);
        try {
            const url = mode === 'login' ? `${API}login` : `${API}register`;
            const body =
                mode === 'login'
                    ? { email: email.trim(), password }
                    : {
                          first_name: firstName.trim(),
                          last_name: lastName.trim() || null,
                          email: email.trim(),
                          password,
                          password_confirmation: passwordConfirm,
                          anrede,
                          user_type: userType,
                      };

            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify(body),
            });
            await handleAuthResult(res, 'Anmeldung fehlgeschlagen.');
        } catch (e: any) {
            setError(e.message || 'Etwas ist schiefgelaufen.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div style={styles.cardShadow}>
            <div style={styles.card}>
                {onClose && (
                    <button onClick={onClose} aria-label="Schließen" style={styles.closeBtn}>
                        ✕
                    </button>
                )}

                <div style={styles.logoWrap}>
                    <img src="/mainevents_logo.svg" alt="Main Event" style={{ height: 44 }} />
                </div>

                <h2 style={styles.title}>{title}</h2>
                <p style={styles.subtitle}>{subtitle}</p>

                {/* Google */}
                <div style={{ display: 'flex', justifyContent: 'center', minHeight: 44 }}>
                    <div ref={googleBtnRef} />
                </div>

                <button
                    type="button"
                    disabled={busy}
                    onClick={handleFacebookLogin}
                    style={{ ...styles.facebookBtn, opacity: busy ? 0.6 : 1 }}
                >
                    Mit Facebook anmelden
                </button>

                <div style={styles.dividerRow}>
                    <span style={styles.dividerLine} />
                    <span style={styles.dividerText}>oder mit E-Mail</span>
                    <span style={styles.dividerLine} />
                </div>

                <form onSubmit={submit}>
                    {mode === 'register' && (
                        <>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <input
                                    style={{ ...styles.input, flex: 1 }}
                                    placeholder="Vorname"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    autoComplete="given-name"
                                />
                                <input
                                    style={{ ...styles.input, flex: 1 }}
                                    placeholder="Nachname"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    autoComplete="family-name"
                                />
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <select
                                    style={{ ...styles.input, flex: 1 }}
                                    value={anrede}
                                    onChange={(e) => setAnrede(Number(e.target.value) as 1 | 2)}
                                >
                                    <option value={1}>Herr</option>
                                    <option value={2}>Frau</option>
                                </select>
                                <select
                                    style={{ ...styles.input, flex: 1 }}
                                    value={userType}
                                    onChange={(e) => setUserType(Number(e.target.value) as 1 | 2)}
                                >
                                    <option value={1}>Privat</option>
                                    <option value={2}>Veranstalter</option>
                                </select>
                            </div>
                        </>
                    )}

                    <input
                        style={styles.input}
                        type="email"
                        placeholder="E-Mail"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                    />
                    <input
                        style={styles.input}
                        type="password"
                        placeholder="Passwort"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                    />
                    {mode === 'register' && (
                        <input
                            style={styles.input}
                            type="password"
                            placeholder="Passwort bestätigen"
                            value={passwordConfirm}
                            onChange={(e) => setPasswordConfirm(e.target.value)}
                            autoComplete="new-password"
                        />
                    )}

                    {error && <div style={styles.error}>{error}</div>}

                    <button type="submit" disabled={busy} style={{ ...styles.primaryBtn, opacity: busy ? 0.6 : 1 }}>
                        {busy ? 'Bitte warten…' : mode === 'login' ? 'Anmelden' : 'Konto erstellen'}
                    </button>
                </form>

                <div style={styles.switchRow}>
                    {mode === 'login' ? (
                        <span>
                            Noch kein Konto?{' '}
                            <button
                                type="button"
                                style={styles.linkBtn}
                                onClick={() => {
                                    setMode('register');
                                    setError(null);
                                }}
                            >
                                Registrieren
                            </button>
                        </span>
                    ) : (
                        <span>
                            Bereits ein Konto?{' '}
                            <button
                                type="button"
                                style={styles.linkBtn}
                                onClick={() => {
                                    setMode('login');
                                    setError(null);
                                }}
                            >
                                Anmelden
                            </button>
                        </span>
                    )}
                </div>

                <p style={styles.legal}>
                    Mit der Anmeldung akzeptierst du unsere{' '}
                    <a href="/datenschutz" style={styles.legalLink}>
                        Datenschutzerklärung
                    </a>{' '}
                    und das{' '}
                    <a href="/impressum" style={styles.legalLink}>
                        Impressum
                    </a>
                    .
                </p>
            </div>
            {reactivationPayload && (
    <div style={styles.modalOverlay}>
        <div style={styles.modalCard}>
            <h3 style={styles.modalTitle}>Account wieder aktivieren?</h3>
            <p style={styles.modalText}>
                Dieser Account wurde gelöscht. Du kannst ihn wieder aktivieren, um fortzufahren.
            </p>

            <div style={styles.modalActions}>
                <button
                    type="button"
                    style={styles.modalSecondaryBtn}
                    disabled={reactivationBusy}
                    onClick={() => setReactivationPayload(null)}
                >
                    Abbrechen
                </button>
                <button
                    type="button"
                    style={{ ...styles.modalPrimaryBtn, opacity: reactivationBusy ? 0.6 : 1 }}
                    disabled={reactivationBusy}
                    onClick={handleReactivateAccount}
                >
                    {reactivationBusy ? 'Bitte warten…' : 'Wieder aktivieren'}
                </button>
            </div>
        </div>
    </div>
)}
        </div>
    );
};

// ── Full-screen route wrapper (/login) ───────────────────────────────────────
const LoginScreen: React.FC = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const redirect = params.get('redirect') || '/profile';

    useEffect(() => {
        if (isLoggedIn()) navigate(redirect, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div style={styles.screen}>
            <div style={{ width: '100%', maxWidth: 420 }}>
                <LoginCard onSuccess={() => navigate(redirect, { replace: true })} />
            </div>
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    screen: {
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
        background: '#EEF1F8',
        fontFamily: FONT_FAMILY,
        boxSizing: 'border-box',
    },
    cardShadow: {
        width: '100%',
        borderRadius: 24,
        background: CARD_BG,
        boxShadow: '0 12px 32px rgba(27,37,89,0.14)',
        fontFamily: FONT_FAMILY,
    },
    card: {
        position: 'relative',
        padding: '28px 22px 22px',
        borderRadius: 24,
        background: CARD_BG,
        boxSizing: 'border-box',
    },
    closeBtn: {
        position: 'absolute',
        top: 14,
        right: 14,
        width: 30,
        height: 30,
        borderRadius: 15,
        border: 'none',
        background: '#E7EBF5',
        color: TEXT_MUTED,
        cursor: 'pointer',
        fontSize: 14,
        lineHeight: 1,
    },
    logoWrap: { display: 'flex', justifyContent: 'center', marginBottom: 12, marginTop: 4 },
    title: { margin: '0 0 6px', fontSize: 22, fontWeight: 900, textAlign: 'center', color: TEXT_DARK },
    subtitle: { margin: '0 0 18px', fontSize: 13.5, lineHeight: 1.5, textAlign: 'center', color: TEXT_MUTED },
    dividerRow: { display: 'flex', alignItems: 'center', gap: 10, margin: '16px 0' },
    dividerLine: { flex: 1, height: 1, background: '#DCE2F0' },
    dividerText: { fontSize: 12, color: TEXT_MUTED, whiteSpace: 'nowrap' },
    input: {
        width: '100%',
        boxSizing: 'border-box',
        padding: '12px 14px',
        marginBottom: 10,
        borderRadius: 12,
        border: '1px solid #D5DEEC',
        background: '#fff',
        fontSize: 15,
        color: TEXT_DARK,
        outline: 'none',
        fontFamily: FONT_FAMILY,
    },
    primaryBtn: {
        width: '100%',
        padding: '13px 14px',
        marginTop: 4,
        borderRadius: 12,
        border: 'none',
        background: ACCENT,
        color: '#fff',
        fontSize: 16,
        fontWeight: 800,
        cursor: 'pointer',
        fontFamily: FONT_FAMILY,
    },
    error: {
        background: '#FDECEC',
        color: '#C0392B',
        borderRadius: 10,
        padding: '8px 12px',
        fontSize: 13,
        marginBottom: 10,
    },
    switchRow: { marginTop: 16, textAlign: 'center', fontSize: 13.5, color: TEXT_MUTED },
    linkBtn: {
        border: 'none',
        background: 'none',
        color: ACCENT,
        fontWeight: 700,
        cursor: 'pointer',
        fontSize: 13.5,
        padding: 0,
    },
    legal: { marginTop: 16, marginBottom: 0, fontSize: 11.5, lineHeight: 1.5, textAlign: 'center', color: TEXT_MUTED },
    legalLink: { color: ACCENT, fontWeight: 600, textDecoration: 'underline' },
    facebookBtn: {
    width: '100%',
    padding: '12px 14px',
    marginTop: 10,
    borderRadius: 12,
    border: 'none',
    background: '#1877F2',
    color: '#fff',
    fontSize: 15,
    fontWeight: 800,
    cursor: 'pointer',
    fontFamily: FONT_FAMILY,
},
modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    zIndex: 9999,
},
modalCard: {
    width: '100%',
    maxWidth: 380,
    borderRadius: 18,
    background: '#F6F7FB',
    padding: 22,
    boxShadow: '0 18px 46px rgba(27,37,89,0.24)',
},
modalTitle: {
    margin: '0 0 8px',
    color: TEXT_DARK,
    fontSize: 20,
    fontWeight: 900,
},
modalText: {
    margin: '0 0 18px',
    color: TEXT_MUTED,
    fontSize: 14,
    lineHeight: 1.5,
},
modalActions: {
    display: 'flex',
    gap: 10,
},
modalSecondaryBtn: {
    flex: 1,
    padding: '12px 14px',
    borderRadius: 12,
    border: 'none',
    background: '#E7EBF5',
    color: TEXT_DARK,
    fontWeight: 800,
    cursor: 'pointer',
},
modalPrimaryBtn: {
    flex: 1,
    padding: '12px 14px',
    borderRadius: 12,
    border: 'none',
    background: ACCENT,
    color: '#fff',
    fontWeight: 800,
    cursor: 'pointer',
},
};

export default LoginScreen;
